import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth";
import { useParams } from "react-router-dom";
import { getUserById } from "../../models/UserFunctions";
import { getOrgById } from "../../models/OrgFunctions";
import Icon1 from "../../icons/Icon";
import Space from "../../layout/Space";
import { getDocDataById, queryCollection } from "../../models/DataFunctions";
import { useAppContext } from "../../config/AppContext";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi";
import TextField2T from "../../textfields/TextField2T";
import PrimaryButton from "../../buttons/PrimaryButton";
import { EasySwitch } from "../../textfields/EasySwitch";
import { User } from "../../types/user";
import { Org } from "../../types/org";
import type { User as FirebaseUser } from "firebase/auth";

interface OrgDetailProps {
  userData?: User;
  orgData?: Org;
  handleLoginAs?: (id: string) => void;
  navigateWorkspace: (path: string, org?: Org | null) => void;
}

function OrgDetail({
  userData: propsUserData,
  orgData: propsOrgData,
  handleLoginAs,
  navigateWorkspace,
}: OrgDetailProps) {
  const { id } = useParams();
  const { currentUser, loading } = useAuth() as {
    currentUser: FirebaseUser | null;
    loading: boolean;
  };

  const [userData, setUserData] = useState<User | null>(propsUserData || null);
  const [orgData, setOrgData] = useState<Org | null>(propsOrgData || null);
  const [actualOrgData, setActualOrgData] = useState<Org | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [totalCarnets, setTotalCarnets] = useState<number>(0);
  const [activatedCarnets, setActivatedCarnets] = useState<number>(0);
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [canCreateCarnets, setCanCreateCarnets] = useState<boolean>(false);

  const { appState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  // Fetch org data and related statistics
  const handleFetchData = async () => {
    try {
      if (!currentUser) return;

      // Fetch current user data
      const user = (await getUserById(currentUser.uid)) as User;
      if (!user) {
        console.error("User not found");
        return;
      }
      setUserData(user);

      // Fetch current user's org
      const org = (await getOrgById(user.org_id)) as Org;
      if (!org) {
        console.error("Org not found");
        return;
      }
      setOrgData(org);

      // Fetch the actual org being viewed
      const actualOrg = (await getOrgById(id || "")) as Org;
      if (!actualOrg) {
        console.error("Org not found");
        navigateWorkspace("/admin/orgs");
        return;
      }
      setActualOrgData(actualOrg);
      setCredits(actualOrg.logement?.facturation?.credits || 0);
      setCanCreateCarnets(actualOrg.logement?.access?.can_create || false);

      // Fetch org users
      const users = (await queryCollection("user", [
        { field: "org_id", operator: "==", value: actualOrg.id },
      ])) as User[];
      setOrgUsers(users);

      // Fetch carnets statistics
      // Master carnets (where this org is the master)
      const masterCarnets = (await queryCollection("carnet", [
        { field: "master_id", operator: "==", value: actualOrg.id },
      ])) as any[];

      // Partner carnets (where this org is a partner)
      const partnerCarnets = (await queryCollection("carnet", [
        { field: "partners_id", operator: "array-contains", value: actualOrg.id },
      ])) as any[];

      // Combine and deduplicate carnets
      const allCarnetsMap = new Map();
      [...masterCarnets, ...partnerCarnets].forEach((carnet) => {
        allCarnetsMap.set(carnet.id, carnet);
      });
      const allCarnets = Array.from(allCarnetsMap.values());

      setTotalCarnets(allCarnets.length);
      setActivatedCarnets(
        allCarnets.filter((carnet) => carnet.activated === true).length
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSaveCredits = async () => {
    if (!actualOrgData) return;

    await updateDocDataFieldApi(
      actualOrgData.ref,
      "logement.facturation.credits",
      credits,
      prefix
    );

    alert("Crédits enregistrés avec succès!");
    handleFetchData();
  };

  const handleSaveAccess = async () => {
    if (!actualOrgData) return;

    await updateDocDataFieldApi(
      actualOrgData.ref,
      "logement.access.can_create",
      canCreateCarnets,
      prefix
    );

    alert("Accès enregistrés avec succès!");
    handleFetchData();
  };

  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser, id]);

  // Handle navigation to orgs page
  const gotoOrgs = () => {
    navigateWorkspace("/admin/orgs");
  };

  if (loading || !currentUser || !actualOrgData) {
    return <div></div>;
  }

  return (
    <div className="flex flex-row w-full max-h-full overflow-auto pb-28">
      <div className="flex flex-col h-fit flex-grow pl-4 w-full">
        <Space y={20} />
        <div className="w-full flex flex-row h-full">
          <div
            className="flex flex-row w-full cursor-pointer"
            onClick={gotoOrgs}
          >
            <div className="text-h2 font-semibold text-primary bg-transparent">
              Orgs
            </div>
            <Icon1 icon_name="right" size={15}></Icon1>
          </div>

          <div className="flex-grow flex-row-reverse flex w-full">
            <Space x={10} />
            {handleLoginAs && orgUsers.length > 0 && (
              <PrimaryButton
                className="w-fit"
                onClick={() => handleLoginAs(orgUsers[0].id)}
              >
                Login as
              </PrimaryButton>
            )}
          </div>
        </div>
        <Space y={5} />
        <div className="text-hx font-semibold">{actualOrgData?.name}</div>
        <Space y={5} />
        {actualOrgData.logo && (
          <div className="rounded-xl h-auto w-full flex flex-row">
            <img
              src={actualOrgData.logo}
              className="h-auto max-h-20 object-left w-auto object-contain rounded-xl"
            />
          </div>
        )}
        <Space y={30} />

        {/* Credits Management */}
        <div className="text-h2 font-semibold">Gestion des crédits</div>
        <Space y={10} />
        <div className="flex flex-row gap-2 items-center">
          <TextField2T
            value={credits.toString()}
            label="Crédits"
            onChange={(e) => setCredits(Number(e.target.value) || 0)}
          />
          <PrimaryButton
            className="w-fit"
            onClick={handleSaveCredits}
            disabled={credits === (actualOrgData.logement?.facturation?.credits || 0)}
          >
            Enregistrer
          </PrimaryButton>
        </div>
        <Space y={30} />

        {/* Access Management */}
        <div className="text-h2 font-semibold">Gestion des accès</div>
        <Space y={10} />
        <div className="flex flex-col gap-3">
          <div className="flex flex-row gap-2 items-center">
            <EasySwitch
              checked={canCreateCarnets}
              onChange={(checked: boolean) => setCanCreateCarnets(checked)}
            />
            <div className="text-h3">Peut créer des carnets</div>
          </div>
          <PrimaryButton
            className="w-fit"
            onClick={handleSaveAccess}
            disabled={canCreateCarnets === (actualOrgData.logement?.access?.can_create || false)}
          >
            Enregistrer les accès
          </PrimaryButton>
        </div>
        <Space y={30} />

        {/* Carnet Statistics */}
        <div className="text-h2 font-semibold">Statistiques des carnets</div>
        <Space y={10} />
        <div className="flex flex-col gap-2">
          <div className="text-h3">
            <span className="font-semibold">Total de carnets:</span>{" "}
            {totalCarnets}
          </div>
          <div className="text-h3">
            <span className="font-semibold">Carnets activés:</span>{" "}
            {activatedCarnets}
          </div>
          <div className="text-h3">
            <span className="font-semibold">Carnets non activés:</span>{" "}
            {totalCarnets - activatedCarnets}
          </div>
        </div>
        <Space y={30} />

        {/* Organization Info */}
        <div className="text-h2 font-semibold">Informations</div>
        <Space y={10} />
        <div className="flex flex-col gap-2">
          <div className="text-h3">
            <span className="font-semibold">Type:</span> {actualOrgData.type}
          </div>
          <div className="text-h3">
            <span className="font-semibold">Email:</span> {actualOrgData.email}
          </div>
          <div className="text-h3">
            <span className="font-semibold">Nombre d'utilisateurs:</span>{" "}
            {orgUsers.length}
          </div>
          <div className="text-h3">
            <span className="font-semibold">ID:</span> {actualOrgData.id}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrgDetail;
